import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { AssetData } from '@/types/api';

const KEY = ['assets'] as const;

export type AssetInput = {
  type: 'hardware' | 'software' | 'network' | 'other';
  category?: string | null;
  name: string;
  identifier?: string | null;
  manufacturer?: string | null;
  model?: string | null;
  location?: string | null;
  assigned_to_contact_id?: number | null;
  purchased_at?: string | null;
  warranty_expires_at?: string | null;
  cost_cents?: number | null;
  status?: 'active' | 'retired' | 'in_storage' | 'stolen' | 'missing';
  notes?: string | null;
  tags?: string[];
};

export function useClientAssets(
  clientId: number | undefined,
  filters: { type?: string; status?: string; q?: string; page?: number } = {},
) {
  return useQuery({
    queryKey: [...KEY, 'client', clientId, filters],
    enabled: clientId != null,
    queryFn: async (): Promise<Paginated<AssetData>> => {
      const { data } = await api.get<Paginated<AssetData>>(
        `/v1/clients/${clientId}/assets`,
        { params: filters },
      );
      return data;
    },
  });
}

export function useAllAssets(
  filters: {
    type?: string;
    status?: string;
    client_id?: number;
    q?: string;
    page?: number;
  } = {},
) {
  return useQuery({
    queryKey: [...KEY, 'all', filters],
    queryFn: async (): Promise<Paginated<AssetData>> => {
      const { data } = await api.get<Paginated<AssetData>>('/v1/assets', { params: filters });
      return data;
    },
  });
}

export function useExpiringWarranties(within = 90) {
  return useQuery({
    queryKey: [...KEY, 'warranty-expiring', within],
    queryFn: async (): Promise<AssetData[]> => {
      const { data } = await api.get<AssetData[]>('/v1/assets/warranty-expiring', {
        params: { within },
      });
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useTotalAssetCount() {
  return useQuery({
    queryKey: [...KEY, 'total-count'],
    queryFn: async (): Promise<{ count: number }> => {
      const { data } = await api.get<{ count: number }>('/v1/assets/total-count');
      return data;
    },
    staleTime: 5 * 60_000,
  });
}

export function useCreateAsset(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: AssetInput): Promise<AssetData> => {
      await primeCsrf();
      const { data } = await api.post<AssetData>(`/v1/clients/${clientId}/assets`, input);
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateAsset(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      patch,
    }: {
      id: number;
      patch: Partial<AssetInput>;
    }): Promise<AssetData> => {
      await primeCsrf();
      const { data } = await api.patch<AssetData>(
        `/v1/clients/${clientId}/assets/${id}`,
        patch,
      );
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteAsset(clientId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (assetId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/assets/${assetId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUploadAssetAttachment(clientId: number, assetId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, kind }: { file: File; kind: string }): Promise<void> => {
      await primeCsrf();
      const fd = new FormData();
      fd.append('file', file);
      fd.append('kind', kind);
      await api.post(
        `/v1/clients/${clientId}/assets/${assetId}/attachments`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useDeleteAssetAttachment(clientId: number, assetId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mediaId: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/clients/${clientId}/assets/${assetId}/attachments/${mediaId}`);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: KEY });
    },
  });
}
